import { Typography } from '@material-ui/core'

import { getTranslate } from '../../../../localization'
import { SkippedPromotion } from '../../../../types/cart'
import useStyles from './styles/index.styles'

type PromotionVerdictProps = {
  refusal?: SkippedPromotion
  /** The CURRENT check failed — the error belongs to the code in the field right now. */
  failed: boolean
  noChange: boolean
}

/**
 * Why a code did NOT apply. Success is not shown here.
 *
 * This used to render four states — applied, refused, could-not-check, and a resting
 * prompt. The applied case moved to AppliedOffer, which can state the actual saving in
 * money; a line reading "SUMMER25 applied" with no figure was the weakest thing the
 * section could say at its most important moment. The resting prompt ("Enter a code and
 * select Apply to check it") is gone outright: it occupied a permanent line to restate
 * the button beside it.
 *
 * Refusal, check failure, and no-change are mutually exclusive. A refused code does NOT
 * block the booking; Continue remains enabled without that code's discount.
 *
 * `role="alert"` on each: the booker is waiting on this answer before deciding whether to
 * continue, so it is announced immediately rather than on next focus.
 *
 * `errored` is GONE, not merely unused. It existed to suppress the resting prompt for a
 * stale error — one from a previous code, after the booker has typed something new. The
 * resting prompt is gone, so nothing read it, and that window renders nothing either way.
 *
 * An earlier revision kept the prop "in case a caller wants the distinction back" and said
 * so in a comment. That is the rationalisation the dead-code rule exists to refuse, and CI
 * refused it: `'errored' is defined but never used`. If the distinction is ever needed
 * again it can be reintroduced with the caller that needs it, which is the only point at
 * which its shape can be known.
 */
const PromotionVerdict = ({
  refusal,
  failed,
  noChange
}: PromotionVerdictProps) => {
  const classes = useStyles()

  // One row, one message. Chosen before rendering rather than as four sibling blocks each
  // repeating the same wrapper — the states are mutually exclusive, so expressing them as
  // a choice says that, where four independent conditions only implied it.
  const message = refusal
    ? { text: refusal.reasonText, notApplied: true }
    : failed
    ? {
        text: getTranslate(
          'The code could not be checked. You can continue without it.'
        ),
        notApplied: false
      }
    : noChange
    ? {
        text: getTranslate(
          'This code did not reduce the price of this booking.'
        ),
        notApplied: true
      }
    : undefined

  if (!message) {
    return null
  }

  return (
    <div className={classes.codeRow}>
      <Typography variant="body2" className={classes.refusal} role="alert">
        {message.notApplied && (
          <strong>{getTranslate('Code not applied')}. </strong>
        )}
        <span>{message.text}</span>
      </Typography>
    </div>
  )
}

export default PromotionVerdict
